"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, Link } from "react-router-dom"
import { User, MapPin, Phone, Mail, Edit, Save, X, Package, Heart, Settings, LogOut, Plus } from "lucide-react"
import type { RootState } from "../store/store"
import { clearUser } from "../store/slices/authSlice"
import { userService } from "../services/userService"
import LoadingSpinner from "../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import SEOHead from "../components/common/SEOHead"
import { realtimeService } from "../services/firebase"
import type { Order } from "../store/slices/ordersSlice"

interface UserProfile {
  name: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  pincode: string
}

interface SavedAddress {
  id: string
  name: string
  phone: string
  address: string
  city: string
  state: string
  pincode: string
  isDefault: boolean
}

// Helper to slugify strings for URLs
function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const Profile: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const { orders: reduxOrders = [] } = useSelector((state: RootState) => state.orders)
  const { items: wishlistItems = [] } = useSelector((state: RootState) => state.wishlist)
  const { items: cartItems = [] } = useSelector((state: RootState) => state.cart)
  const [orders, setOrders] = useState<Order[]>(reduxOrders)

  const [profile, setProfile] = useState<UserProfile>({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  })
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<"profile" | "orders" | "wishlist" | "addresses">("profile")
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [editingAddress, setEditingAddress] = useState<SavedAddress | null>(null)
  const [newAddress, setNewAddress] = useState<Omit<SavedAddress, "id">>({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    isDefault: false,
  })

  useEffect(() => {
    if (user) {
      loadUserProfile()
      loadSavedAddresses()
    }
  }, [user])

  useEffect(() => {
    if (activeTab === "orders" && user?.uid) {
      const unsubscribe = realtimeService.onUserOrdersChange(user.uid, (newOrders) => {
        setOrders(newOrders)
      })
      return () => unsubscribe()
    }
  }, [activeTab, user?.uid])

  const userOrders = orders.filter((order: Order) => order.userId === user?.uid) || []

  const loadUserProfile = async () => {
    if (!user) return

    try {
      const userData = await userService.getUserData(user.uid)
      if (userData) {
        setProfile({
          name: userData.name || user.name || "",
          email: userData.email || user.email || "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          phone: (userData as any)?.phone || "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          address: (userData as any)?.address || "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          city: (userData as any)?.city || "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          state: (userData as any)?.state || "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pincode: (userData as any)?.pincode || "",
        })
      }
    } catch (error) {
      console.error("Error loading profile:", error)
    }
  }

  const loadSavedAddresses = async () => {
    if (!user) return

    try {
      const addresses = await userService.getSavedAddresses(user.uid)
      setSavedAddresses(addresses || [])
    } catch (error) {
      console.error("Error loading addresses:", error)
    }
  }

  const handleSaveProfile = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      await userService.updateUserProfile(user.uid, profile)
      toast.success("Profile updated successfully!")
      setIsEditing(false)
    } catch (error) {
      console.error("Error updating profile:", error)
      toast.error("Failed to update profile")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveAddress = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      if (editingAddress) {
        await userService.updateSavedAddress(user.uid, editingAddress.id, newAddress)
        toast.success("Address updated successfully!")
      } else {
        await userService.addSavedAddress(user.uid, newAddress)
        toast.success("Address saved successfully!")
      }

      setShowAddressForm(false)
      setEditingAddress(null)
      setNewAddress({
        name: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        isDefault: false,
      })
      loadSavedAddresses()
    } catch (error) {
      console.error("Error saving address:", error)
      toast.error("Failed to save address")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAddress = async (addressId: string) => {
    if (!user) return

    try {
      await userService.deleteSavedAddress(user.uid, addressId)
      toast.success("Address deleted successfully!")
      loadSavedAddresses()
    } catch (error) {
      console.error("Error deleting address:", error)
      toast.error("Failed to delete address")
    }
  }

  const handleSetDefaultAddress = async (addressId: string) => {
    if (!user) return

    try {
      await userService.setDefaultAddress(user.uid, addressId)
      toast.success("Default address updated!")
      loadSavedAddresses()
    } catch (error) {
      console.error("Error setting default address:", error)
      toast.error("Failed to update default address")
    }
  }

  const handleLogout = () => {
    dispatch(clearUser())
    navigate("/")
    toast.success("Logged out successfully")
  }

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "confirmed":
        return "bg-blue-100 text-blue-800"
      case "shipped":
        return "bg-purple-100 text-purple-800"
      case "delivered":
        return "bg-green-100 text-green-800"
      case "cancelled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (!user) {
    navigate("/login")
    return null
  }

  return (
    <>
      <SEOHead
        title="My Profile | Anmol Furniture Bazaar"
        description="View and manage your profile, orders, wishlist, and addresses at Anmol Furniture Bazaar."
        keywords="profile, user profile, orders, wishlist, addresses, account, premium furniture"
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{profile.name}</h3>
                  <p className="text-gray-600">{profile.email}</p>
                </div>

                <nav className="space-y-2">
                  <button
                    onClick={() => setActiveTab("profile")}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "profile" ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Settings className="w-5 h-5" />
                    <span>Profile Settings</span>
                  </button>

                  <button
                    onClick={() => setActiveTab("addresses")}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "addresses" ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <MapPin className="w-5 h-5" />
                    <span>Saved Addresses</span>
                    {savedAddresses.length > 0 && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        {savedAddresses.length}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => setActiveTab("orders")}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "orders" ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Package className="w-5 h-5" />
                    <span>My Orders</span>
                    {userOrders.length > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{userOrders.length}</span>
                    )}
                  </button>

                  <button
                    onClick={() => setActiveTab("wishlist")}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "wishlist" ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Heart className="w-5 h-5" />
                    <span>Wishlist</span>
                    {wishlistItems.length > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{wishlistItems.length}</span>
                    )}
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </nav>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                <h4 className="font-semibold text-gray-900 mb-4">Quick Stats</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Orders</span>
                    <span className="font-semibold">{userOrders.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Saved Addresses</span>
                    <span className="font-semibold">{savedAddresses.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Wishlist Items</span>
                    <span className="font-semibold">{wishlistItems.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Cart Items</span>
                    <span className="font-semibold">{cartItems.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {activeTab === "profile" && (
                <div className="bg-white rounded-xl shadow-md p-8">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit Profile</span>
                      </button>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveProfile}
                          disabled={isLoading}
                          className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {isLoading ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}
                          <span>Save</span>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false)
                            loadUserProfile()
                          }}
                          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={profile.pincode}
                        onChange={(e) => setProfile({ ...profile, pincode: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Address
                      </label>
                      <textarea
                        value={profile.address}
                        onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                        disabled={!isEditing}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={profile.city}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={profile.state}
                        onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "addresses" && (
                <div className="bg-white rounded-xl shadow-md p-8">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Saved Addresses</h2>
                    <button
                      onClick={() => setShowAddressForm(true)}
                      className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add New Address</span>
                    </button>
                  </div>

                  {showAddressForm && (
                    <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-gray-50">
                      <h3 className="text-lg font-semibold mb-4">
                        {editingAddress ? "Edit Address" : "Add New Address"}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={newAddress.name}
                          onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="md:col-span-2">
                          <textarea
                            placeholder="Complete Address"
                            value={newAddress.address}
                            onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="City"
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          type="text"
                          placeholder="State"
                          value={newAddress.state}
                          onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          type="text"
                          placeholder="Pincode"
                          value={newAddress.pincode}
                          onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="isDefault"
                            checked={newAddress.isDefault}
                            onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                            className="mr-2"
                          />
                          <label htmlFor="isDefault" className="text-sm text-gray-700">
                            Set as default address
                          </label>
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-4">
                        <button
                          onClick={handleSaveAddress}
                          disabled={isLoading}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {isLoading ? <LoadingSpinner size="sm" /> : "Save Address"}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddressForm(false)
                            setEditingAddress(null)
                            setNewAddress({
                              name: "",
                              phone: "",
                              address: "",
                              city: "",
                              state: "",
                              pincode: "",
                              isDefault: false,
                            })
                          }}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {savedAddresses.length === 0 ? (
                    <div className="text-center py-12">
                      <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No saved addresses</h3>
                      <p className="text-gray-600">Add an address to make checkout faster</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {savedAddresses.map((address) => (
                        <div
                          key={address.id}
                          className={`border rounded-lg p-4 ${
                            address.isDefault ? "border-primary-500 bg-primary-50" : "border-gray-200"
                          }`}
                        >
                          {address.isDefault && (
                            <span className="inline-block bg-primary-600 text-white text-xs px-2 py-1 rounded-full mb-2">
                              Default
                            </span>
                          )}
                          <h4 className="font-semibold text-gray-900">{address.name}</h4>
                          <p className="text-gray-600">{address.phone}</p>
                          <p className="text-gray-600 mt-2">{address.address}</p>
                          <p className="text-gray-600">
                            {address.city}, {address.state} - {address.pincode}
                          </p>
                          <div className="flex space-x-2 mt-4">
                            <button
                              onClick={() => {
                                setEditingAddress(address)
                                setNewAddress(address)
                                setShowAddressForm(true)
                              }}
                              className="text-primary-600 hover:text-primary-700 text-sm"
                            >
                              Edit
                            </button>
                            {!address.isDefault && (
                              <button
                                onClick={() => handleSetDefaultAddress(address.id)}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteAddress(address.id)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "orders" && (
                <div className="bg-white rounded-xl shadow-md p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8">My Orders</h2>

                  {userOrders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h3>
                      <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
                      <button
                        onClick={() => navigate("/")}
                        className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Start Shopping
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {userOrders.map((order: Order) => (
                        <Link to={`/orders/${order.id}`} key={order.id} className="block">
                          <div className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h3 className="font-semibold text-gray-900">Order #{order.id}</h3>
                                <p className="text-sm text-gray-600">
                                  {(() => {
                                    if (typeof order.createdAt === "string") {
                                      return new Date(order.createdAt).toLocaleDateString();
                                    } else if (order.createdAt && typeof order.createdAt === "object" && "seconds" in order.createdAt) {
                                      // @ts-expect-error Firestore Timestamp object may have 'seconds' property
                                      return new Date(order.createdAt.seconds * 1000).toLocaleDateString();
                                    }
                                    return "N/A";
                                  })()}
                                </p>
                              </div>
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${getOrderStatusColor(order.status)}`}
                              >
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </span>
                            </div>
                            <div className="space-y-3 mb-4">
                              {order.items.map((item: Order["items"][number]) => (
                                <div key={item.id} className="flex items-center space-x-3">
                                  <img
                                    src={item.image || "/placeholder.svg?height=48&width=48"}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded-lg"
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">{item.name}</p>
                                    <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                                  </div>
                                  <p className="font-semibold">₹{(item.price * item.quantity).toLocaleString()}</p>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                              <span className="text-gray-600">Total Amount</span>
                              <span className="text-xl font-bold text-primary-600">₹{order.total.toLocaleString()}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "wishlist" && (
                <div className="bg-white rounded-xl shadow-md p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8">My Wishlist</h2>

                  {wishlistItems.length === 0 ? (
                    <div className="text-center py-12">
                      <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Your wishlist is empty</h3>
                      <p className="text-gray-600 mb-6">Save items you love for later</p>
                      <button
                        onClick={() => navigate("/")}
                        className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Browse Products
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {wishlistItems.map((item) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                          <img
                            src={item.image || "/placeholder.svg?height=192&width=192"}
                            alt={item.name}
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <h3 className="font-semibold text-gray-900 mb-2">{item.name}</h3>
                          <p className="text-lg font-bold text-primary-600 mb-4">₹{item.price.toLocaleString()}</p>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/${slugify(item.category)}/${slugify(item.name)}`)}
                              className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                            >
                              View Product
                            </button>
                            <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Profile
